<script setup lang="ts">
    import {computed} from 'vue';
    import ChatCheckmarkIcon from './ChatCheckmarkIcon.vue';

    const props = defineProps<{status: Record<string, any>}>();

    const badgeClass = computed(() => {
        const classes: Record<string, string> = {
            orange: 'label-warning',
            red: 'label-danger',
            blue: 'label-primary',
            green: 'label-success',
        };

        return classes[props.status.statusColor] ?? 'label-primary';
    });

    /** "2025-09-14" -> "14 september". Anything the browser can't parse is shown as it arrived. */
    function dutchDate(isoDate: string | null): string {
        if (!isoDate) {
            return '';
        }

        const date = new Date(String(isoDate));

        return Number.isNaN(date.getTime()) ? String(isoDate) : date.toLocaleDateString('nl-NL', {day: 'numeric', month: 'long'});
    }

    /**
     * The design draws four fixed steps (besteld, ingepakt, onderweg, bezorgd). OrderStatus carries
     * four dates and no per-event log, so the timeline is built from the dates that actually exist:
     * a step is done when its own date is filled in, and the delivery step falls back to the
     * expected date as the still-open last step. "Ingepakt in ons magazijn" and "Onderweg naar je"
     * are left out - nothing in the order payload reports either, and inferring them from the other
     * dates would be a guess dressed up as status.
     */
    const steps = computed(() => {
        const timeline: Array<{label: string; date: string; done: boolean}> = [
            {label: 'Besteld', date: dutchDate(props.status.orderDate), done: true},
        ];

        if (props.status.pickupDate) {
            timeline.push({label: 'Klaar om op te halen', date: dutchDate(props.status.pickupDate), done: true});
        }

        timeline.push(
            props.status.deliveredDate
                ? {label: 'Bezorgd', date: dutchDate(props.status.deliveredDate), done: true}
                : {
                      label: 'Bezorgd',
                      date: props.status.expectedDeliveryDate ? `Verwacht ${dutchDate(props.status.expectedDeliveryDate)}` : '',
                      done: false,
                  }
        );

        return timeline;
    });
</script>

<template>
    <div class="panel panel-default panel-body">
        <div class="order__header">
            <div class="c-panel__heading">Bestelling #{{ status.orderId }}</div>
            <span
                class="label text-uppercase"
                :class="badgeClass">
                {{ status.status || 'Onbekend' }}
            </span>
        </div>
        <div
            v-if="status.orderDate"
            class="order__placed text-muted">
            Geplaatst op {{ dutchDate(status.orderDate) }}
            <!-- The design also prints the line count and order total here; OrderStatus carries
                 neither, so only the date the order actually reports is shown. -->
        </div>

        <ol class="order__timeline list-unstyled">
            <li
                v-for="(step, index) in steps"
                :key="step.label"
                class="order__step"
                :class="{
                    'order__step--last': index === steps.length - 1,
                    'order__step--open-next': !steps[index + 1]?.done,
                }">
                <span class="order__marker">
                    <ChatCheckmarkIcon
                        v-if="step.done"
                        :size="22" />
                    <span
                        v-else
                        class="order__dot"></span>
                </span>
                <span class="order__step-body">
                    <span
                        class="order__step-label"
                        :class="{'order__step-label--open': !step.done}">
                        {{ step.label }}
                    </span>
                    <span
                        v-if="step.date"
                        class="order__step-date text-muted">
                        {{ step.date }}
                    </span>
                </span>
            </li>
        </ol>

        <a
            v-if="status.trackAndTraceUrl"
            class="c-button--black btn-block order__track"
            :href="status.trackAndTraceUrl"
            target="_blank"
            rel="noopener noreferrer">
            <span>Volg je pakket</span>
            <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.6"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true">
                <path d="M9 18l6-6-6-6" />
            </svg>
        </a>
    </div>
</template>

<style scoped lang="scss">
    @import '../../sass/globals';
    .order__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
    }

    .order__placed {
        margin-top: 3px;
        font-size: 13px;
    }

    .order__timeline {
        margin: 14px 0 0;
    }

    .order__step {
        display: flex;
        gap: 14px;
        align-items: stretch;
    }

    .order__marker {
        display: flex;
        flex: 0 0 22px;
        flex-direction: column;
        align-items: center;
        width: 22px;
    }

    /* The connector stops at the last step, so the line never dangles below the timeline, and it
       only runs green as far as the order has actually got. */
    .order__step:not(.order__step--last) .order__marker::after {
        content: '';
        flex: 1;
        width: 2px;
        min-height: 16px;
        background: $green;
    }

    .order__step--open-next .order__marker::after {
        background: #e4e4e4;
    }

    .order__step--last .order__step-body {
        padding-bottom: 0;
    }

    .order__dot {
        width: 22px;
        height: 22px;
        border: 2px solid $grey;
        border-radius: 11px;
        background: $white;
    }

    .order__step-body {
        display: block;
        flex: 1;
        padding-bottom: 12px;
    }

    .order__step-label {
        display: block;
        font-size: 15px;
        font-weight: 700;
        color: $black;
    }

    .order__step-label--open {
        color: #8c8c8c;
    }

    .order__step-date {
        display: block;
        margin-top: 1px;
        font-size: 13px;
    }

    .chat .order__track {
        margin-top: 14px;
        color: $white;
    }
</style>
